<template>
  <div v-if="race"
    class="bg-white/5 backdrop-blur-lg rounded-2xl p-8 text-white border border-white/10 relative overflow-hidden group hover:bg-white/10 transition duration-300">
    <!-- Circuit Background Image -->
    <div class="absolute inset-0 z-0 opacity-20 group-hover:opacity-30 transition duration-500">
      <img v-if="getCircuitImage(race.Circuit.circuitId)" :src="getCircuitImage(race.Circuit.circuitId)" alt="Circuit"
        class="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500" />
    </div>
    <div class="absolute top-0 right-0 w-64 h-64 bg-red-600/20 rounded-full blur-3xl -mr-32 -mt-32"></div>

    <div class="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
      <div>
        <h2 class="text-xs font-bold text-red-500 uppercase tracking-[0.2em] mb-2 flex items-center">
          <span class="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
          Prossimo Gran Premio
        </h2>
        <h3 class="text-4xl md:text-5xl font-bold mb-2 tracking-tight">{{ race.raceName }}</h3>
        <p class="text-gray-300 text-lg flex items-center">
          <i class="fas fa-map-marker-alt mr-2 text-red-500"></i>
          {{ race.Circuit.circuitName }}
        </p>
      </div>
      <div
        class="w-full md:w-auto text-center md:text-right mt-6 md:mt-0 bg-black/20 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
        <div
          class="text-2xl sm:text-3xl md:text-5xl font-mono font-bold text-white tracking-widest tabular-nums whitespace-nowrap">
          {{ countdown }}
        </div>
        <p class="text-xs text-red-400 uppercase tracking-widest mt-1 text-center md:text-right font-bold">Al Via</p>
      </div>
    </div>

    <div class="relative z-10 grid grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
      <button v-for="(session, name) in sessions" :key="name" @click="openSessionModal(name)"
        class="relative overflow-hidden group flex flex-col items-center justify-center p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-red-600/20 hover:border-red-500/50 transition-all duration-300 text-center hover:shadow-[0_0_15px_rgba(220,38,38,0.3)] hover:-translate-y-1">

        <p class="text-[10px] md:text-xs text-red-400 uppercase tracking-widest font-bold mb-2">{{
          formatSessionName(name) }}</p>
        <p class="font-bold text-xl md:text-2xl mb-1 text-white">{{ formatTime(session.date, session.time) }}</p>
        <p class="text-xs text-gray-400 font-medium mb-2">{{ formatDate(session.date) }}</p>

        <!-- Indicatore cliccabile -->
        <span
          class="mt-auto text-[14px] text-white opacity-60 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <i class="fas fa-info-circle"></i>
        </span>
      </button>
    </div>
  </div>
  <div v-else class="bg-white/5 backdrop-blur-md rounded-2xl p-8 text-white border border-white/10 animate-pulse">
    <div class="h-8 bg-white/10 rounded w-1/3 mb-4"></div>
    <div class="h-4 bg-white/10 rounded w-1/4"></div>
  </div>

  <SessionResultsModal :is-open="isModalOpen" :session-name="selectedSessionName" :race="race"
    @close="closeSessionModal" />
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { getCircuitImage } from '../utils/circuitImages';
import SessionResultsModal from './SessionResultsModal.vue';

export default {
  name: 'NextRaceCard',
  components: {
    SessionResultsModal
  },
  props: {
    race: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const timeLeft = ref({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    let interval = null;

    const isModalOpen = ref(false);
    const selectedSessionName = ref(null);

    const openSessionModal = (name) => {
      selectedSessionName.value = name;
      isModalOpen.value = true;
    };

    const closeSessionModal = () => {
      isModalOpen.value = false;
      selectedSessionName.value = null;
    };

    const sessions = computed(() => {
      if (!props.race) return {};
      const { FirstPractice, SecondPractice, ThirdPractice, Qualifying, Sprint } = props.race;
      // Filter out undefined sessions and keep order
      const s = {};
      if (FirstPractice) s.FirstPractice = FirstPractice;
      if (SecondPractice) s.SecondPractice = SecondPractice;
      if (ThirdPractice) s.ThirdPractice = ThirdPractice;
      if (props.race.SprintQualifying) s.SprintQualifying = props.race.SprintQualifying;
      if (Sprint) s.Sprint = Sprint;
      if (Qualifying) s.Qualifying = Qualifying;
      s.Race = { date: props.race.date, time: props.race.time };
      return s;
    });

    const calculateTimeLeft = () => {
      if (!props.race) return;

      const raceDate = new Date(`${props.race.date}T${props.race.time}`);
      const now = new Date();
      const difference = raceDate - now;

      if (difference > 0) {
        timeLeft.value = {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
    };

    const countdown = computed(() => {
      const { days, hours, minutes, seconds } = timeLeft.value;
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    });

    onMounted(() => {
      calculateTimeLeft();
      interval = setInterval(calculateTimeLeft, 1000);
    });

    onUnmounted(() => {
      clearInterval(interval);
    });

    const formatSessionName = (name) => {
      const map = {
        FirstPractice: 'Prove Libere 1',
        SecondPractice: 'Prove Libere 2',
        ThirdPractice: 'Prove Libere 3',
        SprintQualifying: 'Qualifiche Sprint',
        Qualifying: 'Qualifiche',
        Sprint: 'Sprint',
        Race: 'Gara'
      };
      return map[name] || name;
    };

    const formatTime = (date, time) => {
      if (!date || !time) return 'TBA';
      // Ensure time string is treated as UTC if it doesn't already have a timezone designator
      const timeString = time.endsWith('Z') ? time : `${time}Z`;
      const d = new Date(`${date}T${timeString}`);
      return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    }

    return {
      sessions,
      countdown,
      formatSessionName,
      formatTime,
      formatDate,
      getCircuitImage,
      isModalOpen,
      selectedSessionName,
      openSessionModal,
      closeSessionModal
    };
  }
}
</script>
